<template>
  <div class="sp-finder-filter" :class="classModifiers">
    <component
      :is="filter"
      ref="filterInstance"
      v-bind="filterAttrs()"
      :model-value="model"
      :non-clearable="nonClearable"
      :open="open"
      @update:model-value="handleFilterModelUpdate"
      @update:open="handleFilterOpenUpdate"
    >
      <template v-if="hasBarChrome" #activator="activatorProps">
        <FinderFilterChip v-bind="activatorProps" :active="active" />
      </template>
    </component>

    <sp-button
      v-if="showClearAction"
      class="clear-action"
      icon="close-bold"
      color="white"
      size="xx-small"
      densitysmcompact="false"
      :label="clearActionLabel"
      :data-testid="TestId.finder.filterClear"
      @click="deactivate"
    ></sp-button>
  </div>
</template>

<script>
import AvailabilityFilter from "#ce/shared/filters/availability/components/AvailabilityFilter.ce.vue";
import DateFilter from "#ce/shared/filters/date/components/DateFilter.ce.vue";
import CategoryFilter from "#ce/shared/filters/category/components/CategoryFilter.ce.vue";
import DistanceFilter from "#ce/shared/filters/distance/components/DistanceFilter.ce.vue";
import InsurancePlanFilter from "#ce/shared/filters/insurance-plan/components/InsurancePlanFilter.ce.vue";
import ProviderFilter from "#ce/shared/filters/provider/components/ProviderFilter.ce.vue";
import SpecialtyFilter from "#ce/shared/filters/specialty/components/SpecialtyFilter.ce.vue";
import VisitTypeFilter from "#ce/shared/filters/visit-type/components/VisitTypeFilter.ce.vue";

/**
 * All available filters.
 * The filter instance will be dynamically loaded based on the filter type prop.
 *
 * @type {Object}
 */
const availableFilters = {
  availability: AvailabilityFilter,
  category: CategoryFilter,
  insurance: InsurancePlanFilter,
  distance: DistanceFilter,
  date: DateFilter,
  specialty: SpecialtyFilter,
  provider: ProviderFilter,
  visitType: VisitTypeFilter,
};
</script>

<script setup>
import { computed, nextTick, onMounted, provide, ref, useAttrs, useTemplateRef, watch } from "vue";
import { useExpose } from "#ce/composables/useExpose";
import { HOST_INJECTION_KEY, useHostElement } from "#ce/composables/useHostElement";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { formAssociatedHost } from "#ce/framework/utils";
import {
  BAR_CHROME,
  FILTER_CHROME_ANCESTORS,
  FILTER_CHROME_ATTRIBUTE,
  FINDER_FILTER_BAR_TAG,
} from "#ce/shared/finder-filter-protocol";
import { tAccessibleName } from "#ce/shared/components-i18n";
import { forwardableAttrs } from "#ce/utils/forwarded-attrs";
import { booleanAttribute, isPresent, toBoolean, trimToUndefined } from "#ce/utils/props";
import { TestId } from "#/test-ids";
import { useFinderFilterAnnouncement } from "./composables/useFinderFilterAnnouncement";
import FinderFilterChip from "./components/FinderFilterChip.ce.vue";

// The filters submit form values via useForm() on this app's host.
// Every attribute is forwarded to the filter explicitly, so fallthrough would copy each one onto
// the shadow root's wrapper as well.
defineOptions({ ...formAssociatedHost(), inheritAttrs: false });

const emit = defineEmits(["update-active", "update-open", "change", "close"]);
const { emitBubbling } = useHostEmit();

const attrs = useAttrs();

// `hidden` is written onto this host, so forwarding it would hide the filter inside it as well.
// A chip's menu hangs under the chip, so it takes no pointer.
function filterAttrs() {
  const forwardable = forwardableAttrs(attrs);
  delete forwardable.hidden;

  if (hasBarChrome) {
    forwardable["hide-pointer"] = "";
    forwardable.location = "bottom-left";
  }

  return forwardable;
}

const props = defineProps({
  /**
   * The type of the filter.
   *
   * @type {String} - The type of the filter. One of: availability, category, insurance, distance, date, specialty, provider, visitType.
   * @required
   */
  type: {
    type: String,
    validator: (value) => Object.keys(availableFilters).includes(value),
    required: true,
  },
  /**
   * When set, the filter can't be cleared (legal requirement, see
   * `<sp-required-filters-modal>`). Bound to the wrapper/filter and used to guard
   * the `deactivate()` method exposed for the legacy filter menu's "Reset All".
   */
  nonClearable: booleanAttribute(),
  /**
   * Whether the thing this filter depends on is in place. A filter offering to narrow by
   * sub-specialty is worth showing only once a specialty is chosen; one narrowing by keyword only
   * once a keyword is searched. Omit it for a filter that depends on nothing, which is most of them.
   *
   * While `"unmet"` and inactive, the filter is **withheld**: it hides its host and reports
   * `withheld: true` to its bar. A withheld filter that holds a value shows anyway — a filter the
   * visitor cannot see is one they cannot clear.
   *
   * The doctor finder rewrites this per search, so it is the filter's own attribute rather than
   * something its bar configures.
   *
   * @type {String}
   * @values met, unmet
   */
  prerequisite: {
    type: String,
    default: undefined,
    validator: (value) => ["met", "unmet"].includes(value),
  },
});

const model = ref(undefined);

const hasValue = computed(() => {
  const value = model.value;

  // The multi-param filters model a FormData and rebuild it empty when nothing is selected.
  if (value instanceof FormData) {
    return value.keys().next().done === false;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return value !== undefined && value !== "";
});

// The filters announce their initial model as an immediate update:model-value at setup; the
// host's change reports only what happens after that.
const isSettled = ref(false);

function markSettled() {
  isSettled.value = true;
}

onMounted(() => nextTick(markSettled));

// A scripted write's round-trip through the filter can carry a fresh object (ProviderFilter
// rebuilds a FormData per recomputation), which the identity guard below cannot recognize — the
// scripted entry points hold this window open through their watcher flush instead.
let isScriptedWrite = false;

function writeSilently(write) {
  isScriptedWrite = true;
  write();
  nextTick(() => (isScriptedWrite = false));
}

// The inner filter reports the user's pick through update:model-value. The exposed `value`
// setter and the mount-time attribute seed write the model silently (their round-trips
// arrive back here).
//
// `options.seeded` marks an update that carries the value the mount was given rather than a pick:
// it moves the model and dispatches nothing. A filter that resolves its seed through a request
// sets it, since that update lands after the settle tick.
function handleFilterModelUpdate(value, options) {
  if (value === model.value) {
    return;
  }

  model.value = value;

  if (isSettled.value && !isScriptedWrite && !options?.seeded) {
    emitBubbling("change", value);
  }
}

const host = useHostElement();

// The filters are nested children, so `instance.ce` is unset for them and `useHostElement` falls
// back to injection. Without this they resolve a null host, and `useForm` then mirrors no form
// value and logs nothing.
provide(HOST_INJECTION_KEY, host);

/** The enclosing bar, or null when the filter stands alone. Resolved once, at setup. */
const bar = host?.closest(FINDER_FILTER_BAR_TAG) ?? null;

// The chrome follows the enclosing bar, and a `chrome` attribute already on the host wins, so a
// standalone mount can render either one. The leaf filters style from the attribute, so a derived
// chrome writes it.
const declaredChrome = host?.getAttribute(FILTER_CHROME_ATTRIBUTE);
const hasBarChrome = declaredChrome ? declaredChrome === BAR_CHROME : host?.closest(FILTER_CHROME_ANCESTORS) != null;

if (hasBarChrome) {
  host?.setAttribute(FILTER_CHROME_ATTRIBUTE, BAR_CHROME);
}

const filter = computed(() => availableFilters[trimToUndefined(props.type)]);

const isNonClearable = computed(() => toBoolean(props.nonClearable));

const filterInstance = useTemplateRef("filterInstance");

// A filter that submits background values states its own opinion. The rest are active when they
// hold a value.
const active = computed(() => {
  const declared = filterInstance.value?.active;

  return declared === undefined ? hasValue.value : Boolean(declared);
});

function reportActive(value) {
  emit("update-active", value);
}

// Reporting starts at mount, once the template ref has resolved, so a filter's own opinion shapes
// the first value. A filter active from birth never transitions, so it is announced here.
function startReportingActive() {
  if (active.value) {
    reportActive(true);
  }

  watch(active, reportActive);
}

onMounted(startReportingActive);

// `open` is live state this element owns, so it is not a declared prop: Vue installs a
// non-configurable host accessor for every prop, whose getter reports the attribute. The attribute
// names the state to mount at, and the legacy filter menus read and write the property.
const open = ref(isPresent(attrs.open));

function handleFilterOpenUpdate(value) {
  open.value = value;
}

function adoptOpenAttribute(value) {
  open.value = isPresent(value);
}

watch(() => attrs.open, adoptOpenAttribute);

function reportOpen(value) {
  emit("update-open", value);

  if (value && hasBarChrome) {
    scrollIntoViewIfNeeded();
  } else if (!value) {
    emit("close");
  }
}

watch(open, reportOpen);

function scrollIntoViewIfNeeded() {
  host?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
}

const filterInstanceProps = computed(() => filterInstance.value?.props);

const classModifiers = computed(() => ({ "--is-active": active.value }));

const showClearAction = computed(() => hasBarChrome && active.value && !isNonClearable.value);

const clearActionLabel = computed(() => tAccessibleName("finder_filter_clear_action"));

const { exposeProperties, exposeMethods } = useExpose();

/**
 * Compatability with the filter menu.
 * TODO: Remove once all filters are migrated to the new filter.
 */
exposeProperties({
  isFilterApp: { value: true },
  open: {
    set: (value) => (open.value = toBoolean(value)),
    get: () => open.value,
  },
  prependIcon: {
    get: () => filterInstanceProps.value?.prependIcon,
  },
  value: {
    set: (value) => writeSilently(() => (model.value = trimToUndefined(value))),
    get: () => model.value,
  },
  active: {
    get: () => active.value,
  },
  label: {
    get: () => attrs.label,
  },
  /**
   * Multi-value filters (the Provider panel) own several search params. The
   * doctor-finder bridge reads this list to learn which params the one element
   * carries. Single-value filters leave it undefined and fall back to `name`.
   */
  filterNames: {
    get: () => filterInstance.value?.filterNames,
  },
  /**
   * The current selection's display wording, read by the doctor-finder bridge for the
   * search analytics. Undefined for filters that do not resolve their selection to
   * text, and while nothing is selected.
   *
   * TODO: Remove with the rest of this bridge — https://github.com/combinaut/sparkle/issues/29500
   */
  selectedText: {
    get: () => filterInstance.value?.selectedText,
  },
});

/**
 * Clears the filter, dispatching one host `change` with the cleared state. The dispatch waits for
 * the flush, so the filter has emptied its form value by the time a listener reads the form. A
 * filter that rebuilds an empty model from the clear (the Provider panel) supplies that dispatch
 * through `handleFilterModelUpdate`. When no rebuilt model has arrived by the flush, the host
 * dispatches `undefined` itself. A no-op when nothing is applied or the filter is non-clearable.
 */
function deactivate() {
  if (isNonClearable.value) {
    return;
  }

  // Clearing still runs with nothing applied, to retire a seed whose request is still out. It
  // dispatches nothing then: a filter rebuilds its model on the write, and a rebuilt empty model
  // reports a change the visitor never made.
  if (!hasValue.value) {
    writeSilently(() => (model.value = undefined));

    return;
  }

  model.value = undefined;
  nextTick(() => {
    if (model.value === undefined) {
      emitBubbling("change", undefined);
    }
  });
}

/**
 * Compatability with the filter menu.
 * TODO: Remove once all filters are migrated to the new filter.
 */
exposeMethods({
  deactivate,
  /**
   * Restore a multi-value filter's sub-selections from URL params (deep-link,
   * back/forward). No-op for single-value filters that don't implement it.
   */
  setFilterValues(values) {
    writeSilently(() => filterInstance.value?.setFilterValues?.(values));
  },
  /**
   * Narrows a filter's own options to the given provider-search filters, for the
   * filters a client configures under `filter_scopes`. No-op for filters whose
   * options do not depend on the current search.
   *
   * The scope belongs on the filter that has options to narrow, not on this host.
   * TODO: Replace with a declarative prop — https://github.com/combinaut/sparkle/issues/29500
   *
   * @returns {Promise<Boolean>|undefined} The filter's report of whether its narrowed options
   *   arrived. Undefined where the filter has no options to narrow, or none has resolved yet.
   */
  setScope(values) {
    return filterInstance.value?.setScope?.(values);
  },
});

onMounted(() => {
  if (attrs.value) {
    writeSilently(() => (model.value = trimToUndefined(attrs.value)));
  }
});

const withheld = computed(() => props.prerequisite === "unmet" && !active.value && !hasValue.value);

watch(withheld, (value) => host?.toggleAttribute("hidden", value), { immediate: true });

useFinderFilterAnnouncement(bar, {
  open,
  readState: () =>
    Object.freeze({
      active: active.value,
      open: open.value,
      withheld: withheld.value,
      clearable: !isNonClearable.value,
    }),
  describe: () => ({
    name: attrs.name ?? "",
    label: attrs.label ?? "",
    icon: filterInstanceProps.value?.prependIcon,
  }),
  clear: deactivate,
});
</script>

<style>
:host {
  display: block;
  width: 100%;
}

.sp-finder-filter {
  position: relative;
}

.clear-action {
  position: absolute;
  top: var(--sp-ref-scale-4);
  right: calc(var(--sp-ref-scale-12) / 2);
  opacity: 0.3;
  z-index: 2;
  --sp-comp-button-white-text-color: var(--sp-sys-color-primary) !important;
}

/* The filter-bar menus behave like the legacy filter menus beside them: a 0.5rem gap below the
   chip (SpPopup's 4px default reads as "stuck" next to the native <dialog> ones), and they open
   downward only, because a chip sits directly under the search field and a menu placed above it
   would cover the field. Both tokens inherit into every filter-bar type, and page CSS targeting
   the element overrides them. */
:host([chrome="bar"]) {
  --sp-comp-popup-offset: 0.5rem;
  --sp-comp-popup-flip: false;
}

:host([hidden]) {
  display: none;
}

:host(:focus),
:host(:focus-visible) {
  outline: none;
  box-shadow: none !important;
}
</style>
